import React, { ChangeEvent, Fragment } from "react";
import { FastField, Field, FastFieldProps, FieldProps } from "formik";
import { ReactComponent as CalendarIcon } from "../../../assets/icons-svg/calendar-icon.svg";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { validDateRange } from "../../../utils/sfUtils";
import { DatePicker, MobileDatePicker, TimePicker } from "@mui/lab";
import { IMaskInput } from "react-imask";
import NumberFormat from "react-number-format";
import { ERRORS } from "../../constants/user-info.const";
import { format } from 'date-fns'

const handleTaxStateRequiredError = (errors: any) => {
  return errors?.state || "";
};
const getDecimalCount = (num) => {
  // Convert to String
  const numStr = String(num);
  // String Contains Decimal
  if (numStr.includes(".")) {
    return numStr.split(".")[1].length;
  }
  // String Does Not Contain Decimal
  return 0;
};
const formatToTwoDecimalas = (value): string => {
  const decimalCount = getDecimalCount(value);
  if (decimalCount < 2) {
    return parseFloat(value).toFixed(2);
  }
  return value;
};

const checkForErrors = (meta, fieldName, form): boolean => {
  if (meta.error && meta.touched) {
    return true;
  }
  return false;
};
const displayDateHelperText = (meta, fieldName, form, errorMessage) => {
  if (meta.touched && meta.error && !form.values[fieldName]) {
    return errorMessage;
  }
  if (fieldName && meta.touched && meta.error && form.errors[fieldName]) {
    return form.errors[fieldName];
  }
  return "";
};

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props: any,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value ? formatToTwoDecimalas(values.value) : "",
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
    />
  );
});

const TextMaskCustom = React.forwardRef(function TextMaskCustom(
  props: any,
  ref: any
) {
  const { onChange, mask, definitions, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={mask}
      definitions={{
        "#": { definitions },
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});
interface UiFormControlSelectionProps {
  type:
    | "text"
    | "number"
    | "textarea"
    | "select"
    | "radio"
    | "checkbox"
    | "date"
    | "datetime"
    | "dateMobile"
    | "time"
    | "file"
    | "mask"
    | "toggle"
    | "toggleButton"
    | "stringDate"
    | "currency"
    | "KeyboardDatePicker"
    | "autoComplete";
  fastField?: boolean;
  fieldName?: string;
  placeholder?: string;
  defaultValue?: any;
  label?: string;
  readOnly?: boolean;
  multiple?: boolean;
  optionsData?: any[];
  radioOptionsData?: { value: any; label: any; helperText?: any }[];
  optionKey?: string;
  optionValue?: string;
  datePickerVariant?: "dialog" | "inline" | "static";
  disableDatePickerToolbar?: boolean;
  showToolbar?: boolean;
  dateFormat?: string;
  disableFutureDate?: boolean;
  disablePastDate?: boolean;
  provideDayStartDate?: boolean;
  shouldDisableDateFunction?: any;
  maxDate?: Date;
  minDate?: Date;
  disableTimePickerToolbar?: boolean;
  minutesStep?: number;
  mask?: any;
  onChange?:
    | ((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void)
    | undefined;
  onBlur?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  value?: any;
  rows?: number;
  rowsMax?: number;
  maxLength?: number;
  minLength?: number;
  max?: number;
  step?: number | "any";
  min?: number;
  required?: boolean;
  errorMessage?: string;
  inlineLabel?: any;
  validationFunction?: any;
  labelClass?: string;
  disabled?: boolean;
  dateType?: "string";
  showFloatingLabel?: boolean;
  autoOk?: boolean;
  secondaryTextKey?: string;
  startIcon?: any;
  endIcon?: any;
  onKeyPress?: any;
  error?: boolean;
  helperText?: string;
  onValueChange?: any;
  definitions?: any;
  isIsoDate?: boolean;
}
function DateIcon() {
  return (
    <SvgIcon>
      <CalendarIcon />
    </SvgIcon>
  );
}
export default function UiFormControlSelection({
  type,
  fastField = true,
  fieldName,
  placeholder,
  defaultValue,
  label,
  readOnly,
  multiple,
  optionsData = [],
  radioOptionsData = [],
  optionKey,
  optionValue,
  datePickerVariant = "inline",
  disableDatePickerToolbar = false,
  showToolbar = false,
  dateFormat,
  disableFutureDate,
  disablePastDate,
  provideDayStartDate,
  shouldDisableDateFunction,
  maxDate,
  minDate,
  disableTimePickerToolbar = false,
  minutesStep,
  mask,
  value,
  onChange,
  onBlur,
  rows = 3,
  rowsMax = 5,
  max,
  min,
  maxLength,
  minLength,
  required,
  errorMessage = "Required",
  validationFunction,
  labelClass,
  inlineLabel,
  disabled,
  dateType,
  showFloatingLabel = false,
  autoOk = true,
  secondaryTextKey,
  startIcon,
  endIcon,
  onKeyPress,
  error,
  helperText,
  step,
  onValueChange,
  definitions,
  isIsoDate = false
}: UiFormControlSelectionProps) {
  const getFormControlBasedOnType = ({ field, form = {}, meta = {} }: any) => {
    const getDateValue = (date: any) => {
      if (dateType === "string") {
        return new Date(date).valueOf();
      }
      return date * 1000;
    };

    const setDateValue = (input: any) => {
      if (type === "KeyboardDatePicker") {
        if (!input) return;
        if (
          new Date() < new Date(input) ||
          new Date(input) < new Date(validDateRange.min)
        ) {
          form.setFieldValue(fieldName, "");
          return;
        }
      }
      if (dateType === "string") {
        const dateObj = new Date(input);
        const date =
          dateObj.getDate() < 10 ? `0${dateObj.getDate()}` : dateObj.getDate();
        const month =
          dateObj.getMonth() < 9
            ? `0${dateObj.getMonth() + 1}`
            : dateObj.getMonth() + 1;
        const dateStr = input && `${dateObj.getFullYear()}-${month}-${date}`;
        form.setFieldValue(fieldName, dateStr);
        if (onValueChange) {
          onValueChange(dateStr);
        }
      } else {
        form.setFieldValue(
          fieldName,
          Math.floor(new Date(input).valueOf() / 1000)
        );
        if (onValueChange) {
          onValueChange(Math.floor(new Date(input).valueOf() / 1000));
        }
      }
    };
    switch (type) {
      case "text":
        return (
          <TextField
            fullWidth
            label={showFloatingLabel && label}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            defaultValue={defaultValue}
            inputProps={{
              ...field,
              maxLength,
              minLength,
              readOnly,
              onBlur: (event) => {
                field?.onBlur && field.onBlur(event);
                form?.setFieldValue &&
                  form.setFieldValue(fieldName, event.target.value?.trim());
              },
              onKeyPress: onKeyPress,
            }}
            InputProps={{
              startAdornment: startIcon && (
                <InputAdornment position="start">{startIcon}</InputAdornment>
              ),
              endAdornment: endIcon && (
                <InputAdornment position="end">{endIcon}</InputAdornment>
              ),
            }}
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            helperText={meta.touched || form.submitCount > 0 ? meta.error : ""}
            disabled={disabled}
            onBlur={(event) => {
              if (onBlur) onBlur(event);
              field.onBlur(event);
            }}
          />
        );
      case "number":
        return (
          <TextField
            fullWidth
            label={showFloatingLabel && label}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            inputProps={{
              ...field,
              max,
              min,
              maxLength,
              minLength,
              step,
            }}
            type="number"
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            helperText={meta.touched || form.submitCount > 0 ? meta.error : ""}
            disabled={disabled}
            onBlur={(event) => {
              if (onBlur) onBlur(event);
              field.onBlur(event);
            }}
          />
        );
      case "file":
        return (
          <TextField
            fullWidth
            label={showFloatingLabel && label}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            {...field}
            type="file"
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            helperText={meta.touched || form.submitCount > 0 ? meta.error : ""}
            disabled={disabled}
          />
        );
      case "textarea":
        return (
          <TextField
            fullWidth
            label={showFloatingLabel && label}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            inputProps={{
              ...field,
              onBlur: (event) => {
                field.onBlur(event);
                form.setFieldValue(fieldName, event.target.value?.trim());
              },
            }}
            multiline
            rows={rows}
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            helperText={meta.touched || form.submitCount > 0 ? meta.error : ""}
            disabled={disabled}
          />
        );
      case "select":
        return (
          <FormControl
            variant="outlined"
            fullWidth
            size="small"
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            disabled={disabled}
            className={labelClass}
          >
            {showFloatingLabel && label ? (
              <InputLabel>{label}</InputLabel>
            ) : null}
            <Select
              displayEmpty
              multiple={multiple}
              {...field}
              disabled={disabled}
              label={showFloatingLabel && label ? label : undefined}
            >
              {/*<MenuItem value="" disabled>
               {placeholder || label}
            </MenuItem>*/}
              {optionsData.map((option, index) => (
                <MenuItem
                  key={index}
                  value={optionValue ? option[optionValue] : option}
                >
                  <div>
                    {optionKey ? option[optionKey] : option}
                    {secondaryTextKey && (
                      <>
                        <br />
                        <Typography variant="caption">
                          {option[secondaryTextKey]}
                          {secondaryTextKey
                            .split(".")
                            .reduce((p, c) => p?.[c], option)}
                        </Typography>
                      </>
                    )}
                  </div>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {meta.touched || form.submitCount > 0 ? meta.error : ""}
            </FormHelperText>
          </FormControl>
        );
      case "radio":
        return (
          <FormControl
            fullWidth
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            disabled={disabled}
          >
            <RadioGroup {...field}>
              {radioOptionsData.map((option: any, index) => {
                return (
                  <Fragment key={option.label}>
                    <FormControlLabel
                      key={index}
                      value={option.value}
                      control={<Radio />}
                      label={
                        option?.helperText ? (
                          <div>
                            <label>{option.label}</label>
                            <FormHelperText>
                              {option?.helperText}
                            </FormHelperText>
                          </div>
                        ) : (
                          option.label
                        )
                      }
                      className={labelClass}
                    />
                  </Fragment>
                );
              })}
            </RadioGroup>
            <FormHelperText>
              {meta.touched || form.submitCount > 0 ? meta.error : ""}
            </FormHelperText>
          </FormControl>
        );
      case "toggle":
        return (
          <FormControl
            fullWidth
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
          >
            <Box>
              {optionsData.map((option, index) => {
                return (
                  <Button
                    className="toggle-button"
                    key={index}
                    variant={
                      field.value === option.value ? "contained" : "outlined"
                    }
                    color={
                      field.value === option.value ? "primary" : "secondary"
                    }
                    onClick={() => {
                      form.setFieldValue(field.name, option.value);
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>
            <FormHelperText>
              {meta.touched || form.submitCount > 0 ? meta.error : ""}
            </FormHelperText>
          </FormControl>
        );
      case "checkbox":
        return (
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={field.value}
                disabled={disabled}
                color="default" // dont need this with mat ui 5
                onChange={(value) => {
                  form.setFieldValue(fieldName, value);
                }}
                {...field}
              />
            }
            label={inlineLabel}
          />
        );
      case "datetime":
      case "time":
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              autoOk
              inputVariant="outlined"
              label={showFloatingLabel && label}
              disableToolbar={disableTimePickerToolbar}
              size="small"
              margin="none"
              {...field}
              value={field.value && field.value * 1000}
              onChange={(value: any) => {
                form.setFieldValue(
                  fieldName,
                  Math.floor(new Date(value).valueOf() / 1000)
                );
              }}
              minutesStep={minutesStep}
              disabled={disabled}
              emptyLabel={placeholder || label}
              fullWidth
              helperText={
                meta.touched || form.submitCount > 0 ? meta.error : ""
              }
              error={
                (meta.touched || form.submitCount > 0) &&
                (meta.error ? true : false)
              }
            />
          </LocalizationProvider>
        );
      case "mask":
        return (
          <TextField
            fullWidth
            label={showFloatingLabel && label}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            inputProps={{
              ...field,
              onBlur: (event) => {
                field.onBlur(event);
                field.onChange(event);
                if (minLength && minLength > 0) {
                  form.setFieldValue(
                    fieldName,
                    event.target.value?.replaceAll(/[^a-z0-9]+/gi, "")
                      .length === minLength
                      ? event.target.value?.replaceAll(/[^a-z0-9]+/gi, "")
                      : ""
                  );
                }
              },
              mask,
              definitions,
            }}
            InputProps={{ inputComponent: TextMaskCustom }}
            error={
              (meta.touched || form.submitCount > 0) &&
              (meta.error ? true : false)
            }
            helperText={
              meta.touched || form.submitCount > 0 ? meta.error : helperText
            }
            disabled={disabled}
          />
        );
      case "date":
        return (
          <>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={showFloatingLabel && label}
                inputVariant="outlined"
                value={field.value || null}
                maxDate={maxDate}
                showToolbar={showToolbar}
                emptyLabel={placeholder}
                components={{
                  OpenPickerIcon: DateIcon,
                }}
                {...field}
                onChange={(date: any) => {
                  let formatted = format(date, 'yyyy-MM-dd')
                  isIsoDate ? 
                    form.setFieldValue(fieldName, formatted, false)
                   : form.setFieldValue(fieldName, date, false)
                }}
                disabled={disabled}
                onClose={() => {
                  meta.touched = true;
                  field.touched = true;
                  return field.touched;
                }}
                onError={(reason, value) => {
                  reason
                    ? form.setFieldError(fieldName, ERRORS.inValidDate)
                    : form.setFieldError(fieldName, "");
                }}
                renderInput={(field) => (
                  <TextField
                    {...field}
                    size="small"
                    className={labelClass}
                    name={fieldName}
                    onBlur={() => {
                      meta.touched = true;
                    }}
                    error={checkForErrors(meta, fieldName, form)}
                    helperText={displayDateHelperText(
                      meta,
                      fieldName,
                      form,
                      errorMessage
                    )}
                  />
                )}
              />
            </LocalizationProvider>
          </>
        );
      case "dateMobile":
        return (
          <>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDatePicker
                label={showFloatingLabel && label}
                inputFormat={dateFormat || "DD mm YYYY"}
                inputVariant="outlined"
                value={field.value || null}
                maxDate={maxDate}
                showToolbar={showToolbar}
                emptyLabel={placeholder}
                {...field}
                onChange={(date) => form.setFieldValue(fieldName, date, false)}
                disabled={disabled}
                onClose={() => {
                  meta.touched = true;
                  field.touched = true;
                  return field.touched;
                }}
                renderInput={(field) => (
                  <TextField
                    {...field}
                    size="medium"
                    className={labelClass}
                    name={fieldName}
                    onBlur={() => {
                      meta.touched = true;
                    }}
                    error={meta.touched && meta.error}
                    helperText={meta.touched && meta.error ? errorMessage : ""}
                  />
                )}
              />
            </LocalizationProvider>
          </>
        );
      case "currency":
        return (
          <TextField
            label={showFloatingLabel && label}
            className={labelClass}
            variant="outlined"
            size="small"
            inputProps={{
              ...field,
              maxLength,
              minLength,
              readOnly,
            }}
            //  sx={{ p: 0 }}
            error={
              error ||
              ((meta.touched || form.submitCount > 0) &&
                (meta.error ? true : false))
            }
            helperText={
              helperText ||
              (meta.touched || form.submitCount > 0 ? meta.error : "")
            }
            disabled={disabled}
            onBlur={onBlur}
            onChange={onChange}
            InputProps={{
              inputComponent: NumberFormatCustom,
            }}
          />
        );
      case "autoComplete":
        return (
          <Autocomplete
            {...field}
            sx={{
              width: "100%",
            }}
            autoHighlight
            value={form.values[`${fieldName}`]}
            onChange={(e: object, value: any | null) => {
              form.setFieldValue(fieldName, value);
            }}
            defaultValue={defaultValue}
            size="small"
            options={optionsData}
            getOptionLabel={(option: any) => option.name || ""}
            renderOption={(props, option: any) => {
              return (
                <li {...props}>
                  <div>
                    <span key={option[optionKey ? `${optionKey}` : "id"]}>
                      {option[optionValue ? `${optionValue}` : "name"]}
                    </span>
                  </div>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                margin="normal"
                error={
                  error || Boolean(handleTaxStateRequiredError(form.errors))
                }
                helperText={
                  helperText || handleTaxStateRequiredError(form.errors)
                }
              />
            )}
          />
        );
      default:
        return null;
    }
  };

  const validateProps =
    required && errorMessage
      ? { validate: (value: any) => (!value ? errorMessage : "") }
      : validationFunction
      ? { validate: validationFunction }
      : {};

  return fieldName ? (
    fastField ? (
      <>
        <FastField name={fieldName} {...validateProps}>
          {(props: FastFieldProps<any>) =>
            getFormControlBasedOnType({ ...props })
          }
        </FastField>
      </>
    ) : (
      <>
        <Field name={fieldName} {...validateProps}>
          {(props: FieldProps<any>) => getFormControlBasedOnType({ ...props })}
        </Field>
      </>
    )
  ) : (
    getFormControlBasedOnType({
      field: {
        value,
        onChange,
      },
    })
  );
}
